import React from "react";
import shallow from "zustand/shallow";
import { Grid, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { LicenseDetails } from "./LicenseDetails";
import { LicenseQuotas } from "./LicenseQuotas";
import { Permissions } from "./Permissions";
import { useLicenseSyncMutation, useLicenseUpdateMutation } from "src/hooks/api";
import useTenantsStore from "src/stores/tenant-management.store";
import { handleResponse } from "src/utils/apiService";
import { LicenseDetailsType } from "src/types/License.type";
import { UpdateLicenseModal } from "./UpdateLicenseModal";
import { DataAppSettings } from "./DataAppSettings";
import Secrets from "./Secrets";

const useStyles = makeStyles({
  adminSettingsWrap: {
    width: "100%",
    height: "100%"
  },
  adminSettings: {
    height: "100%",
    minHeight: "calc(100vh - 88px)",
    gap: "24px",
    alignItems: "stretch",
    flexWrap: "nowrap"
  },
  adminSettingsLeftPane: {
    gap: "24px",
    flexWrap: "nowrap"
  },
  secretGrid: {
    height: "calc(100vh - 115px)"
  }
});
export const TenantAdminSettings = ({
  licenseDetails,
  switchTab,
  licenseEnabled
}: {
  licenseEnabled?: boolean;
  licenseDetails: LicenseDetailsType;
  switchTab: () => void;
}) => {
  const classes = useStyles();
  const [openUpdateLicenseModal, setOpenUpdateLicenseModal] = React.useState(false);
  const licenseSyncMutation = useLicenseSyncMutation();
  const licenseUpdateMutation = useLicenseUpdateMutation();
  const toggleTenantsRefresh = useTenantsStore((state) => state.toggleTenantsRefresh, shallow);

  const onLicenseSync = () => {
    licenseSyncMutation.mutate(undefined, {
      onSuccess: () => {
        toggleTenantsRefresh();
        handleResponse({ successMessage: `License Synced Successfully.` });
      },
      onError: () => {
        toggleTenantsRefresh();
        switchTab();
      }
    });
  };

  const onLicenseUpdate = (licenseKey: string) => {
    licenseUpdateMutation.mutate(
      { licenseKey },
      {
        onSuccess: async () => {
          await licenseSyncMutation.mutate(undefined, {
            onError: () => {
              switchTab();
              toggleTenantsRefresh();
            }
          });
          toggleTenantsRefresh();
          handleResponse({ successMessage: `License Updated Successfully.` });
          setOpenUpdateLicenseModal(false);
        }
      }
    );
  };

  return (
    <>
      {licenseDetails.valid ? (
        <Grid container direction="row" spacing={3}>
          <Grid item xs={5} container direction="column" className={classes.adminSettingsLeftPane}>
            {licenseEnabled && (
              <>
                <LicenseDetails
                  licenseDetails={licenseDetails}
                  onSync={onLicenseSync}
                  isSyncInProgress={licenseSyncMutation.isLoading}
                  onUpdate={() => setOpenUpdateLicenseModal(true)}
                />
                <Permissions metadata={licenseDetails.metadata} />
              </>
            )}
            <DataAppSettings />
            {licenseEnabled && <LicenseQuotas metadata={licenseDetails.metadata} />}
          </Grid>

          <Grid className={classes.secretGrid} item container xs={7}>
            <Secrets />
          </Grid>

          {openUpdateLicenseModal && (
            <UpdateLicenseModal
              open={openUpdateLicenseModal}
              onClose={() => setOpenUpdateLicenseModal(false)}
              onUpdate={onLicenseUpdate}
              isSubmitInProgress={licenseUpdateMutation.isLoading}
            />
          )}
        </Grid>
      ) : (
        <Alert severity="error">License is not valid for this tenant</Alert>
      )}
    </>
  );
};
